import { saveAs } from 'file-saver';
import {
  buildModelContextRows,
  buildExecutiveModelContextRows,
  buildExecutiveResultsTableRows,
  buildResultsKpis,
  buildResultsTableRows,
  buildCohortRepaymentsMatrix,
  buildCohortRevenueSections,
  buildEmptyCohortRepaymentsMatrix,
  buildEmptyCohortRevenueSections,
  getResultsTableColumnLabels,
  REPAYMENT_GRACE_PERIOD_LABEL,
  resolveExportMonthLabelsFromState,
} from './buildQuickCashflowExportData';
import {
  appendCohortExportSheets,
  COHORT_SHEET_SUMMARY_TAB_MERGED,
  QUICK_CASHFLOW_SUMMARY_TAB_MERGED,
} from './exportCohortSheetExcel';
import { buildMarketingMonthCashflowTable } from './buildMarketingMonthCashflowTable';
import { EXCEL_THEME, MONEY_NUM_FMT, PCT_NUM_FMT } from './excelTheme';
import { OPTIONAL_SCENARIO_KEYS, SCENARIO_META } from './scenarioMeta';
import {
  SCENARIO_METRICS,
  buildScenarioComparisonData,
} from './buildScenarioComparisonData';

const SHEET_NAMES = ['Summary', 'Cohort repayments', 'Cohort revenue'];
const SCENARIO_COMPARISON_TAB = 'Scenario comparison';

function styleBrandHeader(cell) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  cell.font = { bold: true, color: { argb: EXCEL_THEME.brandText }, size: 11 };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
}

function styleSectionRow(cell, colSpan) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.section },
  };
  cell.font = { bold: true, color: { argb: EXCEL_THEME.sectionText }, size: 11 };
  if (colSpan > 1) {
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  }
}

function stylePerspectiveRow(cell) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  cell.font = {
    bold: true,
    color: { argb: EXCEL_THEME.brandText },
    size: 10,
  };
}

function styleLabelCell(cell, { indent, bold } = {}) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.labelBg },
  };
  cell.font = {
    bold: !!bold,
    size: 11,
    color: { argb: EXCEL_THEME.sectionText },
  };
  cell.alignment = { vertical: 'middle', horizontal: 'left', indent: indent ? 1 : 0 };
}

function applyMoneyFormat(cell, value) {
  cell.value = Number(value) || 0;
  cell.numFmt = MONEY_NUM_FMT;
  if ((Number(value) || 0) < 0) {
    cell.font = { color: { argb: EXCEL_THEME.negative } };
  }
}

function writeSummaryStaticKpis(worksheet, { calcResult, state, kpiStartRow }) {
  const kpis = buildResultsKpis(calcResult, state);

  kpis.forEach((kpi, index) => {
    const blockCol = (index % 4) * 3 + 1;
    const blockRow = kpiStartRow + Math.floor(index / 4) * 3;

    const labelCell = worksheet.getCell(blockRow, blockCol);
    labelCell.value = kpi.label;
    labelCell.font = { size: 10, color: { argb: EXCEL_THEME.annotation } };

    const valueCell = worksheet.getCell(blockRow + 1, blockCol);
    if (kpi.format === 'irr') {
      valueCell.value = Number(kpi.value) || 0;
      valueCell.numFmt = PCT_NUM_FMT;
    } else {
      applyMoneyFormat(valueCell, kpi.value);
    }
    valueCell.font = {
      ...(valueCell.font || {}),
      bold: true,
      size: 14,
      color: { argb: EXCEL_THEME.accent },
    };

    const footerCell = worksheet.getCell(blockRow + 2, blockCol);
    footerCell.value = kpi.footer;
    footerCell.font = { size: 9, color: { argb: EXCEL_THEME.annotation } };
  });
}

function writeSummaryStaticTable(
  worksheet,
  startRow,
  { calcResult, totalCols, headerRow }
) {
  let row = startRow;

  buildResultsTableRows(calcResult).forEach((tableRow) => {
    if (tableRow.type === 'section') {
      const cell = worksheet.getCell(row, 1);
      cell.value = tableRow.label;
      styleSectionRow(cell, totalCols);
      worksheet.mergeCells(row, 1, row, totalCols);
      row += 1;
      return;
    }

    if (tableRow.type === 'spacer') {
      row += 1;
      return;
    }

    if (tableRow.type === 'perspective') {
      const cell = worksheet.getCell(row, 1);
      cell.value = tableRow.label;
      stylePerspectiveRow(cell);
      worksheet.mergeCells(row, 1, row, totalCols);
      row += 1;
      return;
    }

    if (tableRow.type !== 'data') {
      return;
    }

    writeExecutiveStaticTableRow(worksheet, row, {
      label: tableRow.label,
      values: tableRow.values,
      bold: tableRow.bold,
      indent: tableRow.indent,
      totalValue: tableRow.totalValue,
      totalCols,
      format: tableRow.format || 'money',
    });
    row += 1;
  });

  worksheet.views = [
    { state: 'frozen', ySplit: headerRow, xSplit: 1, activeCell: 'B2' },
  ];
  worksheet.getColumn(1).width = 28;
  for (let c = 2; c <= totalCols; c += 1) {
    worksheet.getColumn(c).width = 14;
  }

  return row - 1;
}

function writeMarketingMonthCashflowTable(worksheet, startRow, { calcResult, state }) {
  const table = buildMarketingMonthCashflowTable(calcResult, state);
  if (!table) {
    return startRow;
  }

  let row = startRow;

  const titleCell = worksheet.getCell(row, 1);
  titleCell.value = 'Marketing cashflow by month';
  titleCell.font = { bold: true, size: 12, color: { argb: EXCEL_THEME.brand } };
  row += 1;

  styleBrandHeader(worksheet.getCell(row, 1));
  worksheet.getCell(row, 1).value = 'Metric';
  table.columns.forEach((column, columnIndex) => {
    const cell = worksheet.getCell(row, 2 + columnIndex);
    styleBrandHeader(cell);
    cell.value = column.label;
  });
  row += 1;

  table.rows.forEach((tableRow) => {
    const labelCell = worksheet.getCell(row, 1);
    labelCell.value = tableRow.label;
    styleLabelCell(labelCell, { bold: tableRow.highlight });

    table.columns.forEach((column, columnIndex) => {
      const cell = worksheet.getCell(row, 2 + columnIndex);
      const value = column[tableRow.key];
      if (value == null) {
        cell.value = null;
        return;
      }
      if (tableRow.format === 'pct') {
        const pct = Number(value);
        if (!Number.isFinite(pct) || pct <= 0) {
          cell.value = null;
          return;
        }
        cell.value = pct / 100;
        cell.numFmt = pct < 10 ? '0.00%' : '0%';
        return;
      }
      applyMoneyFormat(cell, value);
      if (tableRow.highlight) {
        cell.font = { ...cell.font, bold: true };
      }
    });
    row += 1;
  });

  return row;
}

function writeExecutiveStaticTableRow(
  worksheet,
  rowNum,
  { label, values, bold, indent, totalValue, totalCols, format = 'money' }
) {
  const labelCell = worksheet.getCell(rowNum, 1);
  labelCell.value = label;
  styleLabelCell(labelCell, { bold, indent });

  const formatCell = (cell, value) => {
    if (value == null) {
      cell.value = '—';
      return;
    }
    if (format === 'exposurePct') {
      cell.value = Number(value) / 100;
      cell.numFmt = PCT_NUM_FMT;
      return;
    }
    applyMoneyFormat(cell, value);
    if (bold) {
      cell.font = { ...cell.font, bold: true };
    }
  };

  values.forEach((value, monthIndex) => {
    formatCell(worksheet.getCell(rowNum, 2 + monthIndex), value);
  });

  const totalCell = worksheet.getCell(rowNum, totalCols);
  totalCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.totalCol },
  };
  totalCell.font = { bold: true };

  if (format === 'exposurePct') {
    totalCell.value = '—';
    return;
  }

  const total =
    totalValue != null
      ? totalValue
      : values.reduce((sum, value) => sum + (Number(value) || 0), 0);
  formatCell(totalCell, total);
}

function writeExecutiveSummarySheet(
  workbook,
  worksheet,
  { calcResult, state, calcTimestamp, companyName }
) {
  const monthLabels = getResultsTableColumnLabels(calcResult).monthLabels;
  const totalCols = monthLabels.length + 2;
  let row = 1;

  const titleCell = worksheet.getCell(row, 1);
  titleCell.value = 'Standalone cashflow — monthly';
  titleCell.font = { bold: true, size: 14, color: { argb: EXCEL_THEME.brand } };
  row += 1;

  const noteCell = worksheet.getCell(row, 1);
  noteCell.value =
    'View export: standalone business cashflow without Twelve facility terms. Cohort tabs reflect the model at export.';
  noteCell.font = { size: 9, italic: true, color: { argb: EXCEL_THEME.annotation } };
  worksheet.mergeCells(row, 1, row, 3);
  row += 1;

  buildExecutiveModelContextRows(state, calcTimestamp, { companyName }).forEach(
    ([label, value]) => {
    worksheet.getCell(row, 1).value = label;
    worksheet.getCell(row, 1).font = {
      size: 10,
      color: { argb: EXCEL_THEME.annotation },
    };
    const valueCell = worksheet.getCell(row, 2);
    if (label === 'Opex (monthly)' || label === 'Beginning cash') {
      applyMoneyFormat(valueCell, value);
    } else if (label === 'GPM %' || label === 'Revenue growth YoY %') {
      valueCell.value = Number(value) / 100;
      valueCell.numFmt = '0.00%';
    } else {
      valueCell.value = value;
    }
      row += 1;
    }
  );

  row += 1;
  const headerRow = row;
  styleBrandHeader(worksheet.getCell(row, 1));
  worksheet.getCell(row, 1).value = 'Metric';
  monthLabels.forEach((label, index) => {
    const cell = worksheet.getCell(row, 2 + index);
    styleBrandHeader(cell);
    cell.value = label;
  });
  const totalHeaderCell = worksheet.getCell(row, totalCols);
  styleBrandHeader(totalHeaderCell);
  totalHeaderCell.value = 'Total';
  row += 1;

  buildExecutiveResultsTableRows(calcResult).forEach((tableRow) => {
    if (tableRow.type === 'section') {
      const cell = worksheet.getCell(row, 1);
      cell.value = tableRow.label;
      styleSectionRow(cell, totalCols);
      worksheet.mergeCells(row, 1, row, totalCols);
      row += 1;
      return;
    }
    if (tableRow.type !== 'data') {
      return;
    }
    writeExecutiveStaticTableRow(worksheet, row, {
      label: tableRow.label,
      values: tableRow.values,
      bold: tableRow.bold,
      indent: tableRow.indent,
      totalValue: tableRow.totalValue,
      totalCols,
      format: tableRow.format,
    });
    row += 1;
  });

  worksheet.views = [
    { state: 'frozen', ySplit: headerRow, xSplit: 1, activeCell: 'B2' },
  ];
  worksheet.getColumn(1).width = 28;
  for (let c = 2; c <= totalCols; c += 1) {
    worksheet.getColumn(c).width = 14;
  }
}

function writeSummarySheet(
  workbook,
  worksheet,
  { calcResult, state, calcTimestamp, companyName }
) {
  const monthLabels = calcResult
    ? getResultsTableColumnLabels(calcResult).monthLabels
    : resolveExportMonthLabelsFromState(state);
  const totalCols = monthLabels.length + 2;
  let row = 1;

  const titleCell = worksheet.getCell(row, 1);
  titleCell.value = 'Quick Cash Flow — Results';
  titleCell.font = { bold: true, size: 14, color: { argb: EXCEL_THEME.brand } };
  row += 1;

  const noteCell = worksheet.getCell(row, 1);
  noteCell.value =
    'Snapshot of calculated results at export. Cohort tabs reflect the model at export.';
  noteCell.font = { size: 9, italic: true, color: { argb: EXCEL_THEME.annotation } };
  worksheet.mergeCells(row, 1, row, 3);
  row += 1;

  buildModelContextRows(state, calcTimestamp, { companyName }).forEach(([label, value]) => {
    worksheet.getCell(row, 1).value = label;
    worksheet.getCell(row, 1).font = {
      size: 10,
      color: { argb: EXCEL_THEME.annotation },
    };
    const valueCell = worksheet.getCell(row, 2);
    if (label === 'Facility size' || label === 'Opex (monthly)' || label === 'Beginning cash') {
      applyMoneyFormat(valueCell, value);
    } else if (label === 'Facility fee %' || label === 'GPM %' || label === 'Fee / target XIRR %') {
      valueCell.value = Number(value) / 100;
      valueCell.numFmt = '0.00%';
    } else if (
      label === REPAYMENT_GRACE_PERIOD_LABEL ||
      label === 'Facility fee installments'
    ) {
      valueCell.value = Number(value) || 0;
    } else {
      valueCell.value = value;
    }
    row += 1;
  });

  if (calcResult?.monthlyFeePct != null) {
    worksheet.getCell(row, 1).value = 'Monthly fee %';
    worksheet.getCell(row, 1).font = {
      size: 10,
      color: { argb: EXCEL_THEME.annotation },
    };
    const monthlyFeeCell = worksheet.getCell(row, 2);
    monthlyFeeCell.value = (Number(calcResult.monthlyFeePct) || 0) / 100;
    monthlyFeeCell.numFmt = '0.00%';
    row += 1;
  }

  row += 1;
  worksheet.getCell(row, 1).value = 'Key metrics';
  worksheet.getCell(row, 1).font = {
    bold: true,
    size: 12,
    color: { argb: EXCEL_THEME.brand },
  };
  row += 1;

  if (!calcResult) {
    worksheet.getCell(row, 1).value = 'P&L and Twelve balance — monthly';
    worksheet.getCell(row, 1).font = {
      bold: true,
      size: 12,
      color: { argb: EXCEL_THEME.brand },
    };
    row += 1;

    const monthLabels = resolveExportMonthLabelsFromState(state);
    const totalCols = monthLabels.length + 2;
    styleBrandHeader(worksheet.getCell(row, 1));
    worksheet.getCell(row, 1).value = 'Metric';
    monthLabels.forEach((label, index) => {
      const cell = worksheet.getCell(row, 2 + index);
      styleBrandHeader(cell);
      cell.value = label;
    });
    if (monthLabels.length > 0) {
      const totalHeaderCell = worksheet.getCell(row, totalCols);
      styleBrandHeader(totalHeaderCell);
      totalHeaderCell.value = 'Total';
    }

    worksheet.views = [
      { state: 'frozen', ySplit: row, xSplit: 1, activeCell: 'B2' },
    ];
    worksheet.getColumn(1).width = 28;
    for (let c = 2; c <= Math.max(totalCols, 2); c += 1) {
      worksheet.getColumn(c).width = 14;
    }
    return;
  }

  const kpiStartRow = row;
  writeSummaryStaticKpis(worksheet, { calcResult, state, kpiStartRow });
  row += 6;

  worksheet.getCell(row, 1).value = 'P&L and Twelve balance — monthly';
  worksheet.getCell(row, 1).font = {
    bold: true,
    size: 12,
    color: { argb: EXCEL_THEME.brand },
  };
  row += 1;

  const headerRow = row;
  styleBrandHeader(worksheet.getCell(row, 1));
  worksheet.getCell(row, 1).value = 'Metric';
  monthLabels.forEach((label, i) => {
    const cell = worksheet.getCell(row, 2 + i);
    styleBrandHeader(cell);
    cell.value = label;
  });
  const totalHeaderCell = worksheet.getCell(row, totalCols);
  styleBrandHeader(totalHeaderCell);
  totalHeaderCell.value = 'Total';
  row += 1;

  const lastTableRow = writeSummaryStaticTable(worksheet, row, {
    calcResult,
    totalCols,
    headerRow,
  });

  writeMarketingMonthCashflowTable(worksheet, lastTableRow + 2, {
    calcResult,
    state,
  });
}

function writeMatrixSheet(worksheet, { header, rows, moneyColumnStart }) {
  let row = 1;
  const totalCols = header.length;

  const writeHeader = () => {
    header.forEach((text, colIndex) => {
      const cell = worksheet.getCell(row, colIndex + 1);
      cell.value = text;
      styleBrandHeader(cell);
    });
    row += 1;
  };

  writeHeader();

  rows.forEach((rowValues) => {
    const isTotalRow = rowValues[0] === 'Total';

    rowValues.forEach((value, colIndex) => {
      const cell = worksheet.getCell(row, colIndex + 1);
      if (
        colIndex >= moneyColumnStart &&
        colIndex < totalCols - 1 &&
        typeof value === 'number'
      ) {
        applyMoneyFormat(cell, value);
      } else if (colIndex === totalCols - 1 && typeof value === 'number') {
        applyMoneyFormat(cell, value);
      } else {
        cell.value = value;
      }
      if (isTotalRow) {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: EXCEL_THEME.totalCol },
        };
      }
    });
    row += 1;
  });

  worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 1, activeCell: 'B2' }];
  worksheet.getColumn(1).width = 12;
  worksheet.getColumn(2).width = 16;
  worksheet.getColumn(3).width = 14;
  for (let c = 4; c <= totalCols; c += 1) {
    worksheet.getColumn(c).width = 12;
  }
}

function writeCohortRevenueSheet(worksheet, calcResult) {
  const { sections, moneyColumnStart } = buildCohortRevenueSections(calcResult);
  let row = 1;

  sections.forEach((section, sectionIndex) => {
    if (section.title) {
      const cell = worksheet.getCell(row, 1);
      cell.value = section.title;
      styleSectionRow(cell, section.header.length);
      worksheet.mergeCells(row, 1, row, section.header.length);
      row += 1;
    }
    section.header.forEach((text, colIndex) => {
      const cell = worksheet.getCell(row, colIndex + 1);
      cell.value = text;
      styleBrandHeader(cell);
    });
    row += 1;

    section.rows.forEach((rowValues) => {
      const isTotalRow = rowValues[0] === 'Total';
      rowValues.forEach((value, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        if (
          colIndex >= moneyColumnStart &&
          colIndex < section.header.length - 1 &&
          typeof value === 'number'
        ) {
          applyMoneyFormat(cell, value);
        } else if (
          colIndex === section.header.length - 1 &&
          typeof value === 'number'
        ) {
          applyMoneyFormat(cell, value);
        } else {
          cell.value = value;
        }
        if (isTotalRow) {
          cell.font = { bold: true };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_THEME.totalCol },
          };
        }
      });
      row += 1;
    });

    if (sectionIndex < sections.length - 1) {
      row += 1;
    }
  });

  const colCount = sections[0]?.header?.length || 1;
  worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 1, activeCell: 'B2' }];
  worksheet.getColumn(1).width = 12;
  worksheet.getColumn(2).width = 16;
  worksheet.getColumn(3).width = 14;
  for (let c = 4; c <= colCount; c += 1) {
    worksheet.getColumn(c).width = 12;
  }
}

function appendEmptyCashflowSheets(
  workbook,
  state,
  calcTimestamp,
  summaryTabName,
  companyName
) {
  const summarySheet = workbook.addWorksheet(summaryTabName);
  writeSummarySheet(workbook, summarySheet, {
    calcResult: null,
    state,
    calcTimestamp,
    companyName,
  });

  const monthLabels = resolveExportMonthLabelsFromState(state);
  const repaymentsSheet = workbook.addWorksheet(SHEET_NAMES[1]);
  writeMatrixSheet(
    repaymentsSheet,
    buildEmptyCohortRepaymentsMatrix(monthLabels)
  );

  const revenueSheet = workbook.addWorksheet(SHEET_NAMES[2]);
  const { sections, moneyColumnStart } =
    buildEmptyCohortRevenueSections(monthLabels);
  writeCohortRevenueSheetFromSections(revenueSheet, sections, moneyColumnStart);
}

function writeCohortRevenueSheetFromSections(
  worksheet,
  sections,
  moneyColumnStart
) {
  let row = 1;

  sections.forEach((section, sectionIndex) => {
    if (section.title) {
      const cell = worksheet.getCell(row, 1);
      cell.value = section.title;
      styleSectionRow(cell, section.header.length);
      worksheet.mergeCells(row, 1, row, section.header.length);
      row += 1;
    }
    section.header.forEach((text, colIndex) => {
      const cell = worksheet.getCell(row, colIndex + 1);
      cell.value = text;
      styleBrandHeader(cell);
    });
    row += 1;

    section.rows.forEach((rowValues) => {
      const isTotalRow = rowValues[0] === 'Total';
      rowValues.forEach((value, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        if (
          colIndex >= moneyColumnStart &&
          colIndex < section.header.length - 1 &&
          typeof value === 'number'
        ) {
          applyMoneyFormat(cell, value);
        } else if (
          colIndex === section.header.length - 1 &&
          typeof value === 'number'
        ) {
          applyMoneyFormat(cell, value);
        } else {
          cell.value = value;
        }
        if (isTotalRow) {
          cell.font = { bold: true };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_THEME.totalCol },
          };
        }
      });
      row += 1;
    });

    if (sectionIndex < sections.length - 1) {
      row += 1;
    }
  });

  const colCount = sections[0]?.header?.length || 1;
  worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 1, activeCell: 'B2' }];
  worksheet.getColumn(1).width = 12;
  worksheet.getColumn(2).width = 16;
  worksheet.getColumn(3).width = 14;
  for (let c = 4; c <= colCount; c += 1) {
    worksheet.getColumn(c).width = 12;
  }
}

/** Add the Summary / Cohort repayments / Cohort revenue trio for one scenario. */
function appendCashflowSheetSet(
  workbook,
  {
    calcResult,
    state,
    calcTimestamp,
    companyName,
    summaryTabName,
    repaymentsTabName,
    revenueTabName,
  }
) {
  const summarySheet = workbook.addWorksheet(summaryTabName);
  writeSummarySheet(workbook, summarySheet, {
    calcResult,
    state,
    calcTimestamp,
    companyName,
  });

  const repaymentsSheet = workbook.addWorksheet(repaymentsTabName);
  writeMatrixSheet(repaymentsSheet, buildCohortRepaymentsMatrix(calcResult));

  const revenueSheet = workbook.addWorksheet(revenueTabName);
  writeCohortRevenueSheet(revenueSheet, calcResult);
}

/** One data block (months down, scenarios across) per comparison metric. */
function writeScenarioComparisonSheet(worksheet, { scenarioMap }) {
  let row = 1;

  const titleCell = worksheet.getCell(row, 1);
  titleCell.value = 'Scenario comparison — metrics by month';
  titleCell.font = { bold: true, size: 14, color: { argb: EXCEL_THEME.brand } };
  row += 1;

  const noteCell = worksheet.getCell(row, 1);
  noteCell.value =
    'Chart-ready: select a metric block and insert a line chart in Excel to compare scenarios.';
  noteCell.font = { size: 9, italic: true, color: { argb: EXCEL_THEME.annotation } };
  worksheet.mergeCells(row, 1, row, 4);
  row += 2;

  SCENARIO_METRICS.forEach((metric) => {
    const { data, activeKeys } = buildScenarioComparisonData(
      scenarioMap,
      metric.field
    );
    const hasValues = data.some((entry) =>
      activeKeys.some((key) => entry[key] != null)
    );
    if (!hasValues) {
      return;
    }

    const metricCell = worksheet.getCell(row, 1);
    metricCell.value = metric.label;
    metricCell.font = { bold: true, size: 12, color: { argb: EXCEL_THEME.brand } };
    row += 1;

    styleBrandHeader(worksheet.getCell(row, 1));
    worksheet.getCell(row, 1).value = 'Month';
    activeKeys.forEach((key, index) => {
      const cell = worksheet.getCell(row, 2 + index);
      styleBrandHeader(cell);
      cell.value = SCENARIO_META[key].label;
    });
    row += 1;

    data.forEach((entry) => {
      worksheet.getCell(row, 1).value = entry.month;
      activeKeys.forEach((key, index) => {
        const cell = worksheet.getCell(row, 2 + index);
        const value = entry[key];
        if (value == null) {
          cell.value = null;
          return;
        }
        if (metric.format === 'percent') {
          cell.value = Number(value) / 100;
          cell.numFmt = PCT_NUM_FMT;
          return;
        }
        applyMoneyFormat(cell, value);
      });
      row += 1;
    });

    row += 1;
  });

  worksheet.views = [
    { state: 'frozen', ySplit: 3, xSplit: 1, activeCell: 'B4' },
  ];
  worksheet.getColumn(1).width = 14;
  for (let c = 2; c <= 5; c += 1) {
    worksheet.getColumn(c).width = 16;
  }
}

export async function buildQuickCashflowWorkbook({
  calcResult = null,
  state,
  calcTimestamp,
  cohortExportOptions = null,
  executiveView = false,
  scenarioResults = null,
  includeCashflowSheets = true,
}) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Twelve Cockpit';
  workbook.created = new Date();

  const hasCashflowData = Boolean(calcResult);
  const companyName = cohortExportOptions?.companyName?.trim() || null;
  const includeCohortSheets = Boolean(
    cohortExportOptions?.scorecardConfig && state?.cohortSheet?.cohorts?.length
  );

  if (includeCohortSheets) {
    await appendCohortExportSheets(workbook, state.cohortSheet, {
      ...cohortExportOptions,
      ExcelJS,
      summaryTabName: COHORT_SHEET_SUMMARY_TAB_MERGED,
    });
  }

  const cashflowSummaryTabName = includeCohortSheets
    ? QUICK_CASHFLOW_SUMMARY_TAB_MERGED
    : SHEET_NAMES[0];

  if (includeCashflowSheets) {
    if (hasCashflowData && executiveView) {
      const summarySheet = workbook.addWorksheet(cashflowSummaryTabName);
      writeExecutiveSummarySheet(workbook, summarySheet, {
        calcResult,
        state,
        calcTimestamp,
        companyName,
      });
    } else if (hasCashflowData) {
      appendCashflowSheetSet(workbook, {
        calcResult,
        state,
        calcTimestamp,
        companyName,
        summaryTabName: cashflowSummaryTabName,
        repaymentsTabName: SHEET_NAMES[1],
        revenueTabName: SHEET_NAMES[2],
      });

      OPTIONAL_SCENARIO_KEYS.forEach((key) => {
        const scenarioResult = scenarioResults?.[key];
        if (!scenarioResult) {
          return;
        }
        const { label } = SCENARIO_META[key];
        appendCashflowSheetSet(workbook, {
          calcResult: scenarioResult,
          state,
          calcTimestamp,
          companyName,
          summaryTabName: `${SHEET_NAMES[0]} (${label})`,
          repaymentsTabName: `${SHEET_NAMES[1]} (${label})`,
          revenueTabName: `${SHEET_NAMES[2]} (${label})`,
        });
      });

      const hasScenarioSheets = OPTIONAL_SCENARIO_KEYS.some(
        (key) => scenarioResults?.[key]
      );
      if (hasScenarioSheets) {
        const comparisonSheet = workbook.addWorksheet(SCENARIO_COMPARISON_TAB);
        writeScenarioComparisonSheet(comparisonSheet, {
          scenarioMap: {
            base: calcResult,
            bull: scenarioResults?.bull ?? null,
            bear: scenarioResults?.bear ?? null,
          },
        });
      }
    } else {
      appendEmptyCashflowSheets(
        workbook,
        state,
        calcTimestamp,
        cashflowSummaryTabName,
        companyName
      );
    }
  }

  return { workbook, includeCohortSheets, hasCashflowData, includeCashflowSheets };
}

function sanitizeExportFilenamePart(value) {
  return String(value || '')
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildQuickCashflowExportFilename({
  filenameBase = 'quick-cashflow',
  companyName = null,
  includeCohortTabs = false,
}) {
  const modelPart = sanitizeExportFilenamePart(filenameBase) || 'quick-cashflow';
  const companyPart = String(companyName || '').trim();
  const normalizedCompany = sanitizeExportFilenamePart(companyPart).toLowerCase();
  const normalizedModel = modelPart.toLowerCase();
  const companyAlreadyInBase =
    normalizedCompany.length > 0 &&
    (normalizedModel === normalizedCompany ||
      normalizedModel.startsWith(`${normalizedCompany}-`));

  const safeBase =
    companyPart && !companyAlreadyInBase
      ? sanitizeExportFilenamePart(`${companyPart} - ${filenameBase}`) || modelPart
      : modelPart;
  const suffix = includeCohortTabs ? '-export' : '-results';
  return `${safeBase}${suffix}.xlsx`;
}

export async function buildQuickCashflowExcelBlob({
  calcResult = null,
  state,
  calcTimestamp,
  filenameBase = 'quick-cashflow',
  cohortExportOptions = null,
  executiveView = false,
  scenarioResults = null,
  includeCashflowSheets = true,
}) {
  const hasCashflowData = Boolean(calcResult);
  const includeCohortSheets = Boolean(
    cohortExportOptions?.scorecardConfig && state?.cohortSheet?.cohorts?.length
  );
  const canExport =
    includeCohortSheets || (includeCashflowSheets && hasCashflowData);
  if (!canExport) {
    return null;
  }

  const { workbook, includeCohortSheets: hasCohortTabs } =
    await buildQuickCashflowWorkbook({
      calcResult,
      state,
      calcTimestamp,
      cohortExportOptions,
      executiveView,
      scenarioResults,
      includeCashflowSheets,
    });
  const buffer = await workbook.xlsx.writeBuffer();
  const exportFilename = buildQuickCashflowExportFilename({
    filenameBase,
    companyName: cohortExportOptions?.companyName,
    includeCohortTabs: hasCohortTabs,
  });
  return {
    blob: new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename: exportFilename,
  };
}

export async function downloadQuickCashflowExcel({
  calcResult = null,
  state,
  calcTimestamp,
  filenameBase = 'quick-cashflow',
  cohortExportOptions = null,
  executiveView = false,
  scenarioResults = null,
}) {
  const built = await buildQuickCashflowExcelBlob({
    calcResult,
    state,
    calcTimestamp,
    filenameBase,
    cohortExportOptions,
    executiveView,
    scenarioResults,
  });
  if (!built) {
    return;
  }
  saveAs(built.blob, built.filename);
}

export { SHEET_NAMES };
